import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DateRangeIcon from "@material-ui/icons/DateRange";
import PaymentIcon from "@material-ui/icons/Payment";
import ReceiptIcon from "@material-ui/icons/Receipt";
import StarIcon from "@material-ui/icons/Star";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";

import moment from "moment";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import SubscriptionModal from "../../components/SubscriptionModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const PLANS = [
  {
    key: "monthly",
    name: "Korax Mensal",
    price: 297,
    monthlyPrice: 297,
    credits: 2000,
    description: "Ideal para começar com flexibilidade.",
    badge: null
  },
  {
    key: "quarterly",
    name: "Korax Trimestral",
    price: 591,
    monthlyPrice: 197,
    credits: 6000,
    description: "Economize R$ 300 em três meses.",
    badge: "Mais escolhido"
  },
  {
    key: "yearly",
    name: "Korax Anual",
    price: 2148,
    monthlyPrice: 179,
    credits: 24000,
    description: "Economize R$ 1.416 por ano.",
    badge: "Melhor custo"
  }
];

const INCLUDED_FEATURES = [
  "Korax IA treinável",
  "Central de Conversas",
  "CRM e oportunidades",
  "Kanban comercial",
  "Agenda inteligente",
  "Follow-ups",
  "Sequências de mensagens",
  "Catálogo de produtos e serviços",
  "Campanhas",
  "Integrações",
  "Atualizações da plataforma",
  "Suporte Korax"
];

const formatCurrency = value =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflowY: "auto",
    padding: 22,
    background:
      theme.palette.type === "dark"
        ? "#0B1220"
        : "#F5F7FB",
    boxSizing: "border-box",
    ...theme.scrollbarStyles,

    [theme.breakpoints.down("sm")]: {
      padding: 12
    }
  },

  container: {
    width: "100%",
    maxWidth: 1320,
    margin: "0 auto"
  },

  header: {
    marginBottom: 22
  },

  eyebrow: {
    color: "#2563EB",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.1em",
    textTransform: "uppercase"
  },

  title: {
    marginTop: 5,
    color: theme.palette.type === "dark" ? "#F8FAFC" : "#0F172A",
    fontSize: 28,
    lineHeight: 1.2,
    fontWeight: 850,
    letterSpacing: "-0.035em"
  },

  subtitle: {
    maxWidth: 680,
    marginTop: 7,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    fontSize: 14,
    lineHeight: 1.55
  },

  currentPlan: {
    position: "relative",
    overflow: "hidden",
    marginBottom: 24,
    padding: 26,
    borderRadius: 22,
    color: "#FFFFFF",
    background:
      "linear-gradient(135deg, #0F2A66 0%, #155EEF 54%, #0284C7 100%)",
    boxShadow: "0 22px 50px rgba(21,94,239,.22)",

    "&:before": {
      content: '""',
      position: "absolute",
      width: 260,
      height: 260,
      top: -150,
      right: -80,
      borderRadius: "50%",
      background: "rgba(255,255,255,.10)"
    },

    "&:after": {
      content: '""',
      position: "absolute",
      width: 180,
      height: 180,
      right: 100,
      bottom: -130,
      borderRadius: "50%",
      background: "rgba(255,255,255,.07)"
    }
  },

  currentPlanContent: {
    position: "relative",
    zIndex: 2,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: 24,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  currentLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    opacity: 0.78
  },

  currentName: {
    marginTop: 8,
    fontSize: 25,
    fontWeight: 850,
    letterSpacing: "-0.03em"
  },

  currentPrice: {
    marginTop: 8,
    fontSize: 14,
    opacity: 0.9
  },

  currentMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 9,
    marginTop: 18
  },

  currentMetaItem: {
    padding: "8px 12px",
    border: "1px solid rgba(255,255,255,.18)",
    borderRadius: 10,
    background: "rgba(255,255,255,.10)",
    fontSize: 12,
    fontWeight: 700
  },

  activeChip: {
    height: 30,
    color: "#166534",
    background: "#DCFCE7",
    fontWeight: 850
  },

  section: {
    marginTop: 26
  },

  sectionTitle: {
    color: theme.palette.type === "dark" ? "#F8FAFC" : "#0F172A",
    fontSize: 18,
    fontWeight: 820,
    letterSpacing: "-0.02em"
  },

  sectionSubtitle: {
    marginTop: 4,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    fontSize: 13
  },

  plansGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 16,
    marginTop: 15,

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },

  planCard: {
    position: "relative",
    minHeight: 350,
    display: "flex",
    flexDirection: "column",
    padding: 22,
    borderRadius: 18,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(148,163,184,.16)"
        : "1px solid #E2E8F0",
    background: theme.palette.type === "dark" ? "#111C2E" : "#FFFFFF",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 14px 35px rgba(0,0,0,.18)"
        : "0 14px 35px rgba(15,23,42,.07)",
    transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",

    "&:hover": {
      transform: "translateY(-4px)",
      borderColor: "rgba(37,99,235,.45)",
      boxShadow: "0 22px 48px rgba(15,23,42,.12)"
    }
  },

  highlightedPlan: {
    border: "2px solid #2563EB",
    boxShadow: "0 20px 48px rgba(37,99,235,.16)"
  },

  planBadge: {
    position: "absolute",
    top: 14,
    right: 14,
    padding: "5px 9px",
    borderRadius: 999,
    color: "#1D4ED8",
    background: "#DBEAFE",
    fontSize: 10,
    fontWeight: 850
  },

  planName: {
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#334155",
    fontSize: 14,
    fontWeight: 750
  },

  planPrice: {
    marginTop: 18,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#0F172A",
    fontSize: 34,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.045em"
  },

  planPeriod: {
    marginTop: 7,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    fontSize: 12
  },

  planDescription: {
    minHeight: 42,
    marginTop: 17,
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#475569",
    fontSize: 13,
    lineHeight: 1.5
  },

  planBenefit: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 11,
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#475569",
    fontSize: 12.5,
    fontWeight: 650
  },

  planButton: {
    width: "100%",
    minHeight: 44,
    marginTop: "auto",
    borderRadius: 11,
    textTransform: "none",
    fontWeight: 800
  },

  featuresBox: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 10,
    marginTop: 15,
    padding: 18,
    borderRadius: 18,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(148,163,184,.16)"
        : "1px solid #E2E8F0",
    background: theme.palette.type === "dark" ? "#111C2E" : "#FFFFFF",

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
    },

    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr"
    }
  },

  feature: {
    minHeight: 45,
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "9px 10px",
    borderRadius: 10,
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#475569",
    background:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,.06)"
        : "#F8FAFC",
    fontSize: 12.5,
    fontWeight: 650
  },

  featureIcon: {
    color: "#16A34A",
    fontSize: 18
  },

  historyPaper: {
    marginTop: 15,
    overflow: "hidden",
    borderRadius: 18,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(148,163,184,.16)"
        : "1px solid #E2E8F0",
    background: theme.palette.type === "dark" ? "#111C2E" : "#FFFFFF",
    boxShadow: "none"
  },

  historyHeader: {
    padding: "17px 18px",
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(148,163,184,.12)"
        : "1px solid #E2E8F0"
  },

  tableWrap: {
    overflowX: "auto"
  },

  tableHeadCell: {
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em"
  },

  tableCell: {
    color: theme.palette.type === "dark" ? "#CBD5E1" : "#334155",
    fontSize: 12.5
  },

  emptyState: {
    padding: "42px 20px",
    textAlign: "center",
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B"
  },

  checkoutOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 20000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    background: "rgba(2, 6, 23, .72)",
    backdropFilter: "blur(5px)"
  },

  checkoutCard: {
    width: "100%",
    maxWidth: 470,
    padding: 26,
    borderRadius: 22,
    background: "#FFFFFF",
    boxShadow: "0 30px 80px rgba(2,6,23,.35)"
  },

  checkoutTitle: {
    color: "#0F172A",
    fontSize: 20,
    fontWeight: 850,
    letterSpacing: "-0.025em"
  },

  checkoutSubtitle: {
    marginTop: 7,
    color: "#64748B",
    fontSize: 13,
    lineHeight: 1.5
  },

  documentInput: {
    width: "100%",
    height: 46,
    marginTop: 18,
    padding: "0 14px",
    borderRadius: 11,
    border: "1.5px solid #CBD5E1",
    outline: "none",
    boxSizing: "border-box",
    color: "#0F172A",
    background: "#FFFFFF",
    fontFamily: "inherit",
    fontSize: 14,

    "&:focus": {
      borderColor: "#2563EB",
      boxShadow: "0 0 0 4px rgba(37,99,235,.10)"
    }
  },

  checkoutActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr",
    gap: 10,
    marginTop: 20
  }
}));

const SubscriptionPage = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [officialPlans, setOfficialPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("PIX");
  const [pixPayment, setPixPayment] = useState(null);

  const [cardData, setCardData] = useState({
    holderName: "",
    number: "",
    expiryMonth: "",
    expiryYear: "",
    ccv: "",
    phone: "",
    postalCode: "",
    addressNumber: ""
  });

  useEffect(() => {
    const loadPage = async () => {
      if (!user?.companyId) return;

      setLoading(true);

      try {
        const [
          plansResponse,
          subscriptionResponse,
          invoiceResponse
        ] = await Promise.all([
          api.get("/korax-subscription/plans"),
          api.get("/korax-subscription/current"),
          api.get(
            "/korax-subscription/history"
          )
        ]);

        setOfficialPlans(
          Array.isArray(plansResponse.data)
            ? plansResponse.data
            : []
        );

        setCurrentSubscription(
          subscriptionResponse.data?.subscription || null
        );

        setCompany(
          subscriptionResponse.data?.company || null
        );

        setCpfCnpj(
          String(
            subscriptionResponse.data?.company?.document || ""
          ).replace(/\D/g, "")
        );

        const invoiceData = Array.isArray(invoiceResponse.data)
          ? invoiceResponse.data
          : invoiceResponse.data?.invoices || [];

        setInvoices(invoiceData);
      } catch (error) {
        toastError(error);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [user?.companyId]);

  const openPlanCheckout = plan => {
    setPaymentMethod("CREDIT_CARD");

    setCardData({
      holderName: "",
      number: "",
      expiryMonth: "",
      expiryYear: "",
      ccv: "",
      phone: "",
      postalCode: "",
      addressNumber: ""
    });

    setSelectedPlan(plan);
  };

  const closePlanCheckout = () => {
    if (checkoutLoading) return;
    setSelectedPlan(null);
  };

  const subscribeToPlan = async () => {
    const document = cpfCnpj.replace(/\D/g, "");

    if (!document) {
      toast.error("Informe seu CPF ou CNPJ para continuar.");
      return;
    }

    if (!selectedPlan?.id) {
      toast.error("Plano não identificado.");
      return;
    }


    setCheckoutLoading(true);

    try {
      const payload = {
        planId: selectedPlan.id,
        cpfCnpj: document,
        paymentMethod
      };

      const { data } = await api.post(
        "/korax-subscription/create",
        payload
      );

      const returnedMethod = String(
        data?.billingType || paymentMethod
      ).toUpperCase();

      if (returnedMethod === "CREDIT_CARD") {
        if (!data?.invoiceUrl) {
          throw new Error(
            "A página de pagamento do cartão não foi retornada."
          );
        }

        setCurrentSubscription({
          status: data?.status || "pending",
          koraxPlanId: data?.plan?.id,
          recurrence: data?.plan?.recurrence,
          creditsIncluded: data?.plan?.credits,
          lastInvoiceUrl: data.invoiceUrl
        });

        setSelectedPlan(null);

        window.location.href = data.invoiceUrl;

        return;
      }

      if (
        returnedMethod === "PIX" &&
        (!data?.pixQrCode || !data?.pixKey)
      ) {
        throw new Error(
          "O QR Code Pix não foi retornado. Tente novamente."
        );
      }

      if (
        returnedMethod === "BOLETO" &&
        !data?.identificationField &&
        !data?.bankSlipUrl
      ) {
        throw new Error(
          "Os dados do boleto não foram retornados. Tente novamente."
        );
      }

      setPixPayment({
        paymentId: data.paymentId,
        billingType: returnedMethod,
        pixQrCode: data.pixQrCode || null,
        pixKey: data.pixKey || null,
        bankSlipUrl: data.bankSlipUrl || null,
        identificationField: data.identificationField || null,
        status: data.status,
        plan: data.plan
      });

      setCurrentSubscription({
        status: data?.status || "pending",
        koraxPlanId: data?.plan?.id,
        recurrence: data?.plan?.recurrence,
        creditsIncluded: data?.plan?.credits,
        lastInvoiceUrl: null
      });

      setSelectedPlan(null);

      toast.success(
        paymentMethod === "BOLETO" ? "Boleto gerado com sucesso." : "Cobrança Pix gerada com sucesso."
      );
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Não foi possível iniciar a assinatura.";

      toast.error(message);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const openInvoice = invoice => {
    setSelectedInvoice({
      ...invoice,
      value:
        invoice.value !== undefined
          ? Number(invoice.value)
          : 0
    });

    setPaymentModalOpen(true);
  };

  const closeInvoice = () => {
    setPaymentModalOpen(false);
    setSelectedInvoice(null);
  };

  const getStatus = invoice => {
    if (invoice.status === "paid") {
      return {
        label: "Pago",
        color: "#166534",
        background: "#DCFCE7"
      };
    }

    const overdue = moment(invoice.dueDate).isBefore(moment(), "day");

    if (overdue) {
      return {
        label: "Vencido",
        color: "#991B1B",
        background: "#FEE2E2"
      };
    }

    return {
      label: "Em aberto",
      color: "#92400E",
      background: "#FEF3C7"
    };
  };

  const latestInvoice = invoices[0];

  const activePlan = officialPlans.find(
    plan =>
      Number(plan.id) ===
      Number(currentSubscription?.koraxPlanId)
  );

  const dueDate =
    currentSubscription?.nextDueDate ||
    currentSubscription?.expiresAt ||
    company?.dueDate ||
    latestInvoice?.dueDate ||
    null;

  const daysRemaining = dueDate
    ? moment(dueDate).diff(moment(), "days")
    : null;

  if (loading) {
    return (
      <div className={classes.root}>
        <Box
          minHeight="55vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {pixPayment && (
        <div
          className={classes.checkoutOverlay}
          onClick={() => setPixPayment(null)}
        >
          <div
            className={classes.checkoutCard}
            onClick={event => event.stopPropagation()}
            style={{
              maxWidth: 430,
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
              padding: 20
            }}
          >
            <div style={{ textAlign: "center" }}>
              <Typography className={classes.checkoutTitle}>
                {pixPayment.billingType === "BOLETO"
                  ? "Boleto bancário"
                  : "Pagamento Pix"}
              </Typography>

              <Typography className={classes.checkoutSubtitle}>
                {pixPayment.billingType === "BOLETO"
                  ? "Copie a linha digitável ou abra o boleto para concluir sua assinatura."
                  : "Escaneie o QR Code ou copie o código Pix para concluir sua assinatura."}
              </Typography>
            </div>

            <div
              style={{
                marginTop: 14,
                padding: "11px 14px",
                borderRadius: 12,
                textAlign: "center",
                background: "#F8FAFC",
                border: "1px solid #E2E8F0"
              }}
            >
              <div
                style={{
                  color: "#334155",
                  fontSize: 12,
                  fontWeight: 800
                }}
              >
                Korax {pixPayment.plan?.name}
              </div>

              <div
                style={{
                  marginTop: 3,
                  color: "#0F172A",
                  fontSize: 24,
                  fontWeight: 900
                }}
              >
                {formatCurrency(pixPayment.plan?.price)}
              </div>

              <div
                style={{
                  marginTop: 3,
                  color: "#64748B",
                  fontSize: 11
                }}
              >
                {Number(
                  pixPayment.plan?.credits || 0
                ).toLocaleString("pt-BR")} créditos IA inclusos
              </div>
            </div>

            {pixPayment.billingType === "PIX" ? (
              <>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 14
                  }}
                >
                  <img
                    src={`data:image/png;base64,${pixPayment.pixQrCode}`}
                    alt="QR Code Pix"
                    style={{
                      width: 170,
                      height: 170,
                      maxWidth: "100%",
                      borderRadius: 12,
                      border: "1px solid #E2E8F0"
                    }}
                  />
                </div>

                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      marginBottom: 5,
                      color: "#64748B",
                      fontSize: 10,
                      fontWeight: 800
                    }}
                  >
                    PIX COPIA E COLA
                  </div>

                  <div
                    style={{
                      maxHeight: 58,
                      overflowY: "auto",
                      padding: "9px 10px",
                      borderRadius: 9,
                      border: "1px solid #E2E8F0",
                      background: "#F8FAFC",
                      color: "#334155",
                      fontSize: 10.5,
                      lineHeight: 1.35,
                      wordBreak: "break-all"
                    }}
                  >
                    {pixPayment.pixKey}
                  </div>
                </div>

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        pixPayment.pixKey
                      );
                      toast.success("Código Pix copiado!");
                    } catch {
                      toast.error(
                        "Não foi possível copiar automaticamente."
                      );
                    }
                  }}
                  style={{
                    minHeight: 42,
                    marginTop: 12,
                    borderRadius: 10,
                    textTransform: "none",
                    fontWeight: 850
                  }}
                >
                  Copiar código Pix
                </Button>
              </>
            ) : (
              <>
                <div style={{ marginTop: 16 }}>
                  <div
                    style={{
                      marginBottom: 6,
                      color: "#64748B",
                      fontSize: 10,
                      fontWeight: 800
                    }}
                  >
                    LINHA DIGITÁVEL
                  </div>

                  <div
                    style={{
                      padding: "12px",
                      borderRadius: 10,
                      border: "1px solid #E2E8F0",
                      background: "#F8FAFC",
                      color: "#334155",
                      fontSize: 12,
                      lineHeight: 1.5,
                      wordBreak: "break-all"
                    }}
                  >
                    {pixPayment.identificationField ||
                      "Linha digitável disponível no boleto."}
                  </div>
                </div>

                {pixPayment.identificationField && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          pixPayment.identificationField
                        );
                        toast.success(
                          "Linha digitável copiada!"
                        );
                      } catch {
                        toast.error(
                          "Não foi possível copiar automaticamente."
                        );
                      }
                    }}
                    style={{
                      minHeight: 42,
                      marginTop: 12,
                      borderRadius: 10,
                      textTransform: "none",
                      fontWeight: 850
                    }}
                  >
                    Copiar linha digitável
                  </Button>
                )}

                {pixPayment.bankSlipUrl && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      window.open(
                        pixPayment.bankSlipUrl,
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                    style={{
                      minHeight: 42,
                      marginTop: 9,
                      borderRadius: 10,
                      textTransform: "none",
                      fontWeight: 800
                    }}
                  >
                    Visualizar boleto
                  </Button>
                )}
              </>
            )}

            <Button
              fullWidth
              variant="text"
              onClick={() => setPixPayment(null)}
              style={{
                minHeight: 38,
                marginTop: 7,
                borderRadius: 10,
                textTransform: "none",
                color: "#64748B",
                fontWeight: 750
              }}
            >
              Fechar
            </Button>

            <Typography
              style={{
                marginTop: 7,
                textAlign: "center",
                color: "#94A3B8",
                fontSize: 10.5,
                lineHeight: 1.4
              }}
            >
              Seu plano será ativado automaticamente após a confirmação
              do pagamento.
            </Typography>
          </div>
        </div>
      )}

      {selectedPlan && (
        <div
          className={classes.checkoutOverlay}
          onClick={closePlanCheckout}
        >
          <div
            className={classes.checkoutCard}
            onClick={event => event.stopPropagation()}
            style={{
              maxWidth: 520,
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
              padding: 22
            }}
          >
            <Typography className={classes.checkoutTitle}>
              Assinar Korax {selectedPlan.name}
            </Typography>

            <Typography className={classes.checkoutSubtitle}>
              Confirme seu CPF ou CNPJ para gerar o pagamento seguro
              da sua assinatura.
            </Typography>

            <div
              style={{
                marginTop: 16,
                padding: "14px 16px",
                borderRadius: 12,
                background: "#EFF6FF",
                border: "1px solid #DBEAFE"
              }}
            >
              <div
                style={{
                  color: "#1E3A8A",
                  fontSize: 12,
                  fontWeight: 750
                }}
              >
                Korax {selectedPlan.name}
              </div>

              <div
                style={{
                  marginTop: 5,
                  color: "#0F172A",
                  fontSize: 22,
                  fontWeight: 900
                }}
              >
                {formatCurrency(selectedPlan.price)}
              </div>

              <div
                style={{
                  marginTop: 4,
                  color: "#64748B",
                  fontSize: 12
                }}
              >
                {Number(selectedPlan.credits || 0).toLocaleString(
                  "pt-BR"
                )} créditos IA inclusos
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 7,
                marginTop: 18,
                padding: 5,
                borderRadius: 12,
                background: "#F1F5F9"
              }}
            >
              {[
                {
                  key: "CREDIT_CARD",
                  label: "💳 Cartão"
                },
                {
                  key: "PIX",
                  label: "◆ Pix"
                },
                {
                  key: "BOLETO",
                  label: "▤ Boleto"
                }
              ].map(method => (
                <button
                  key={method.key}
                  type="button"
                  onClick={() =>
                    setPaymentMethod(method.key)
                  }
                  style={{
                    minHeight: 42,
                    padding: "5px 7px",
                    border: "none",
                    borderRadius: 9,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 12,
                    fontWeight: 800,
                    color:
                      paymentMethod === method.key
                        ? "#FFFFFF"
                        : "#475569",
                    background:
                      paymentMethod === method.key
                        ? "#1D4ED8"
                        : "transparent",
                    boxShadow:
                      paymentMethod === method.key
                        ? "0 7px 16px rgba(37,99,235,.20)"
                        : "none"
                  }}
                >
                  {method.label}
                </button>
              ))}
            </div>

            <Typography
              style={{
                marginTop: 11,
                color: "#64748B",
                fontSize: 12,
                lineHeight: 1.45
              }}
            >
              {paymentMethod === "CREDIT_CARD"
                ? "Finalize o pagamento com cartão no ambiente seguro de cobrança."
                : paymentMethod === "PIX"
                  ? "A confirmação do Pix normalmente acontece em poucos segundos."
                  : "O boleto terá vencimento em 3 dias e o acesso será liberado após a confirmação."}
            </Typography>

            {paymentMethod === "CREDIT_CARD" && (
              <div
                style={{
                  marginTop: 16,
                  padding: "14px 15px",
                  borderRadius: 11,
                  color: "#334155",
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  fontSize: 12,
                  lineHeight: 1.55
                }}
              >
                <div
                  style={{
                    color: "#0F172A",
                    fontSize: 13,
                    fontWeight: 850,
                    marginBottom: 5
                  }}
                >
                  💳 Pagamento com cartão
                </div>

                Você será direcionado para o ambiente seguro de pagamento
                para informar os dados do cartão e concluir a assinatura.
              </div>
            )}

            <input
              className={classes.documentInput}
              style={{ marginTop: 14 }}
              placeholder="CPF ou CNPJ"
              value={cpfCnpj}
              maxLength={14}
              onChange={event =>
                setCpfCnpj(
                  event.target.value.replace(/[^0-9]/g, "")
                )
              }
            />

            <div className={classes.checkoutActions}>
              <Button
                variant="outlined"
                onClick={closePlanCheckout}
                disabled={checkoutLoading}
                style={{
                  borderRadius: 11,
                  textTransform: "none",
                  fontWeight: 750
                }}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={subscribeToPlan}
                disabled={checkoutLoading}
                style={{
                  borderRadius: 11,
                  textTransform: "none",
                  fontWeight: 800
                }}
              >
                {checkoutLoading
                  ? paymentMethod === "CREDIT_CARD"
                    ? "Processando..."
                    : "Gerando cobrança..."
                  : paymentMethod === "CREDIT_CARD"
                    ? "Continuar com cartão"
                    : paymentMethod === "BOLETO"
                      ? "Gerar boleto"
                      : "Gerar Pix"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <SubscriptionModal
        open={paymentModalOpen}
        onClose={closeInvoice}
        aria-labelledby="form-dialog-title"
        Invoice={selectedInvoice || {}}
        contactId={null}
      />

      <div className={classes.container}>
        <header className={classes.header}>
          <div className={classes.eyebrow}>Conta e cobrança</div>

          <Typography className={classes.title}>
            Assinatura
          </Typography>

          <Typography className={classes.subtitle}>
            Gerencie seu plano Korax, acompanhe sua validade e consulte
            seus pagamentos em um único lugar.
          </Typography>
        </header>

        <section className={classes.currentPlan}>
          <div className={classes.currentPlanContent}>
            <div>
              <div className={classes.currentLabel}>
                Seu plano atual
              </div>

              <div className={classes.currentName}>
                {activePlan ? `Korax ${activePlan.name}` : "Período de teste"}
              </div>

              <div className={classes.currentPrice}>
                {activePlan
                  ? `${formatCurrency(activePlan.price)} por período`
                  : "Explore os recursos da Korax durante seu período de teste."}
              </div>

              <div className={classes.currentMeta}>
                {dueDate && (
                  <div className={classes.currentMetaItem}>
                    Válido até {moment(dueDate).format("DD/MM/YYYY")}
                  </div>
                )}

                {daysRemaining !== null && (
                  <div className={classes.currentMetaItem}>
                    {daysRemaining >= 0
                      ? `${daysRemaining} dias restantes`
                      : `Vencido há ${Math.abs(daysRemaining)} dias`}
                  </div>
                )}
              </div>
            </div>

            <Chip
              icon={<VerifiedUserIcon />}
              label={
                daysRemaining !== null && daysRemaining < 0
                  ? "Expirado"
                  : "Ativo"
              }
              className={classes.activeChip}
            />
          </div>
        </section>

        <section className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Escolha o plano ideal
          </Typography>

          <Typography className={classes.sectionSubtitle}>
            Todos os planos incluem os principais recursos comerciais
            da Korax.
          </Typography>

          <div className={classes.plansGrid}>
            {officialPlans.map(plan => {
              const recurrenceKey = String(
                plan.recurrence || ""
              ).toUpperCase();

              const monthlyPrice =
                recurrenceKey === "YEARLY"
                  ? Number(plan.price) / 12
                  : recurrenceKey === "QUARTERLY"
                    ? Number(plan.price) / 3
                    : Number(plan.price);

              const isQuarterly =
                recurrenceKey === "QUARTERLY";

              const badge = isQuarterly
                ? "Mais escolhido"
                : recurrenceKey === "YEARLY"
                  ? "Melhor custo"
                  : null;

              const description =
                recurrenceKey === "YEARLY"
                  ? "Economize R$ 1.416 por ano."
                  : recurrenceKey === "QUARTERLY"
                    ? "Economize R$ 300 em três meses."
                    : "Ideal para começar com flexibilidade.";

              return (
                <article
                  key={plan.id}
                  className={`${classes.planCard} ${
                    isQuarterly
                      ? classes.highlightedPlan
                      : ""
                  }`}
                >
                  {badge && (
                    <div className={classes.planBadge}>
                      {badge}
                    </div>
                  )}

                  <div className={classes.planName}>
                    Korax {plan.name}
                  </div>

                  <div className={classes.planPrice}>
                    {formatCurrency(plan.price)}
                  </div>

                  <div className={classes.planPeriod}>
                    {recurrenceKey === "MONTHLY"
                      ? "cobrança mensal"
                      : `${formatCurrency(monthlyPrice)} por mês`}
                  </div>

                  <div className={classes.planDescription}>
                    {description}
                  </div>

                  <div className={classes.planBenefit}>
                    <CheckCircleIcon
                      style={{
                        color: "#16A34A",
                        fontSize: 18
                      }}
                    />

                    {Number(
                      plan.credits || 0
                    ).toLocaleString("pt-BR")} créditos IA
                  </div>

                  <div className={classes.planBenefit}>
                    <CheckCircleIcon
                      style={{
                        color: "#16A34A",
                        fontSize: 18
                      }}
                    />

                    Recursos comerciais completos
                  </div>

                  <div className={classes.planBenefit}>
                    <CheckCircleIcon
                      style={{
                        color: "#16A34A",
                        fontSize: 18
                      }}
                    />

                    Suporte e atualizações
                  </div>

                  <Button
                    variant={
                      isQuarterly
                        ? "contained"
                        : "outlined"
                    }
                    color="primary"
                    className={classes.planButton}
                    startIcon={
                      isQuarterly ? <StarIcon /> : null
                    }
                    onClick={() =>
                      openPlanCheckout(plan)
                    }
                  >
                    Escolher plano
                  </Button>
                </article>
              );
            })}
          </div>
        </section>

        <section className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Tudo o que sua operação precisa
          </Typography>

          <Typography className={classes.sectionSubtitle}>
            Uma plataforma completa para organizar, automatizar e
            acompanhar suas vendas.
          </Typography>

          <div className={classes.featuresBox}>
            {INCLUDED_FEATURES.map(feature => (
              <div key={feature} className={classes.feature}>
                <CheckCircleIcon className={classes.featureIcon} />
                {feature}
              </div>
            ))}
          </div>
        </section>

        <section
          id="payment-history"
          className={classes.section}
        >
          <Typography className={classes.sectionTitle}>
            Histórico de pagamentos
          </Typography>

          <Typography className={classes.sectionSubtitle}>
            Consulte cobranças, vencimentos e pagamentos anteriores.
          </Typography>

          <Paper className={classes.historyPaper}>
            <div className={classes.historyHeader}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" style={{ gap: 9 }}>
                  <ReceiptIcon style={{ color: "#2563EB" }} />

                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: 800
                    }}
                  >
                    Pagamentos
                  </Typography>
                </Box>

                <Chip
                  size="small"
                  label={`${invoices.length} registros`}
                />
              </Box>
            </div>

            {invoices.length === 0 ? (
              <div className={classes.emptyState}>
                <ReceiptIcon
                  style={{
                    fontSize: 38,
                    color: "#94A3B8",
                    marginBottom: 10
                  }}
                />

                <Typography
                  style={{
                    fontSize: 14,
                    fontWeight: 750
                  }}
                >
                  Nenhum pagamento encontrado
                </Typography>

                <Typography
                  style={{
                    marginTop: 5,
                    fontSize: 12.5
                  }}
                >
                  Suas próximas cobranças aparecerão aqui.
                </Typography>
              </div>
            ) : (
              <div className={classes.tableWrap}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>
                        Plano
                      </TableCell>

                      <TableCell className={classes.tableHeadCell}>
                        Valor
                      </TableCell>

                      <TableCell className={classes.tableHeadCell}>
                        Vencimento
                      </TableCell>

                      <TableCell className={classes.tableHeadCell}>
                        Status
                      </TableCell>

                      <TableCell
                        className={classes.tableHeadCell}
                        align="right"
                      >
                        Ação
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {invoices.map(invoice => {
                      const status = getStatus(invoice);

                      return (
                        <TableRow key={invoice.id}>
                          <TableCell className={classes.tableCell}>
                            {invoice.detail ||
                              (activePlan
                                ? `Korax ${activePlan.name}`
                                : "Plano Korax")}
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            {formatCurrency(
                              invoice.value || 0
                            )}
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            {moment(invoice.dueDate).format(
                              "DD/MM/YYYY"
                            )}
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            <Chip
                              size="small"
                              label={status.label}
                              style={{
                                color: status.color,
                                background: status.background,
                                fontWeight: 800
                              }}
                            />
                          </TableCell>

                          <TableCell
                            className={classes.tableCell}
                            align="right"
                          >
                            {invoice.status === "paid" ? (
                              <Chip
                                size="small"
                                icon={<CheckCircleIcon />}
                                label="Pago"
                                style={{
                                  color: "#166534",
                                  background: "#DCFCE7",
                                  fontWeight: 800
                                }}
                              />
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<PaymentIcon />}
                                disabled={
                                  !invoice.linkInvoice
                                }
                                onClick={() => {
                                  if (!invoice.linkInvoice) {
                                    return;
                                  }

                                  window.open(
                                    invoice.linkInvoice,
                                    "_blank",
                                    "noopener,noreferrer"
                                  );
                                }}
                                style={{
                                  borderRadius: 9,
                                  textTransform: "none",
                                  fontWeight: 800
                                }}
                              >
                                Pagar
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Paper>
        </section>
      </div>
    </div>
  );
};

export default SubscriptionPage;
